import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, KeyRound, Plus, UserCog, XCircle } from 'lucide-react';
import {
  type ExtranetUser,
  inviteExtranetUser,
  listExtranetUsers,
  setExtranetPassword,
} from '@/api/extranet';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

function isAdmin(role: string | undefined): boolean {
  return role === 'TENANT_ADMIN' || role === 'PLATFORM_ADMIN';
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString();
}

export function ExtranetPage() {
  const { t } = useTranslation();
  const admin = isAdmin(useAuthStore((s) => s.user?.role));
  const [items, setItems] = useState<ExtranetUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [pwTarget, setPwTarget] = useState<ExtranetUser | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    listExtranetUsers()
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!admin) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <p className="rounded-lg border bg-card p-6 text-center text-sm text-muted-foreground">
          {t('extranet.adminOnly')}
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-lop-text-primary)]">
            {t('extranet.title')}
          </h1>
          <p className="text-sm text-[var(--color-lop-text-secondary)]">{t('extranet.subtitle')}</p>
        </div>
        <Button size="sm" onClick={() => setShowInvite(true)} data-testid="extranet-invite">
          <Plus className="mr-1 h-4 w-4" />
          {t('extranet.invite')}
        </Button>
      </header>

      <div className="overflow-hidden rounded-lg border bg-card">
        {loading && <p className="p-6 text-center text-sm text-muted-foreground">{t('common.loading')}</p>}
        {!loading && items.length === 0 && (
          <p className="p-6 text-center text-sm text-muted-foreground">{t('extranet.empty')}</p>
        )}
        {!loading && items.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-[var(--color-lop-text-muted)]">
                <th className="px-4 py-2 font-medium">{t('extranet.col.user')}</th>
                <th className="px-4 py-2 font-medium">{t('extranet.col.status')}</th>
                <th className="px-4 py-2 font-medium">{t('extranet.col.invited')}</th>
                <th className="px-4 py-2 font-medium">{t('extranet.col.lastLogin')}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-lop-border)]">
              {items.map((u) => (
                <tr key={u.id} data-testid="extranet-row">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <UserCog className="h-4 w-4 shrink-0 text-[var(--color-lop-text-muted)]" />
                      <div className="min-w-0">
                        <p className="truncate font-medium text-[var(--color-lop-text-primary)]">{u.name}</p>
                        <p className="truncate text-xs text-[var(--color-lop-text-muted)]">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {u.activatedAt ? (
                      <span className="flex items-center gap-1 text-xs text-emerald-400">
                        <CheckCircle2 className="h-4 w-4" />
                        {t('extranet.status.active')}
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-amber-300">
                        <XCircle className="h-4 w-4" />
                        {t('extranet.status.invited')}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-lop-text-secondary)]">{fmtDate(u.invitedAt)}</td>
                  <td className="px-4 py-3 text-[var(--color-lop-text-secondary)]">{fmtDate(u.lastLoginAt)}</td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPwTarget(u)}
                      data-testid="extranet-set-password"
                    >
                      <KeyRound className="mr-1 h-4 w-4" />
                      {t('extranet.setPassword')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showInvite && (
        <InviteModal
          onClose={() => setShowInvite(false)}
          onInvited={() => {
            setShowInvite(false);
            load();
          }}
        />
      )}
      {pwTarget && <PasswordModal user={pwTarget} onClose={() => setPwTarget(null)} />}
    </div>
  );
}

function InviteModal({ onClose, onInvited }: { onClose: () => void; onInvited: () => void }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!email.trim() || !name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await inviteExtranetUser({ email: email.trim(), name: name.trim() });
      onInvited();
    } catch (err) {
      setError((err as Error)?.message ?? 'Failed to invite');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">{t('extranet.inviteModal.title')}</h3>
        {error && <p className="mb-3 text-sm text-red-400">{error}</p>}
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('extranet.inviteModal.name')}
        </label>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="mb-3" data-testid="extranet-name" />
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('extranet.inviteModal.email')}
        </label>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mb-4"
          data-testid="extranet-email"
        />
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={submit} disabled={submitting} data-testid="extranet-invite-submit">
            {t('extranet.inviteModal.submit')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PasswordModal({ user, onClose }: { user: ExtranetUser; onClose: () => void }) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit() {
    if (password.length < 8) {
      setError(t('extranet.passwordModal.tooShort'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await setExtranetPassword(user.id, password);
      setDone(true);
    } catch (err) {
      setError((err as Error)?.message ?? 'Failed to set password');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-1 text-lg font-semibold">{t('extranet.passwordModal.title')}</h3>
        <p className="mb-4 text-xs text-[var(--color-lop-text-muted)]">{user.email}</p>
        {done ? (
          <>
            <p className="mb-4 flex items-center gap-2 text-sm text-emerald-400">
              <CheckCircle2 className="h-4 w-4" />
              {t('extranet.passwordModal.done')}
            </p>
            <div className="flex justify-end">
              <Button size="sm" onClick={onClose}>
                {t('common.close')}
              </Button>
            </div>
          </>
        ) : (
          <>
            {error && <p className="mb-3 text-sm text-red-400">{error}</p>}
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('extranet.passwordModal.password')}
            </label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mb-4"
              data-testid="extranet-password"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
                {t('common.cancel')}
              </Button>
              <Button size="sm" onClick={submit} disabled={submitting} data-testid="extranet-password-submit">
                {t('extranet.passwordModal.submit')}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
